
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, LineChart, Check, BarChart3, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GridBackground from '@/components/GridBackground';
import LeadCaptureForm from '@/components/LeadCaptureForm';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';

const BrokerNovosUsuarios = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [campaignSource, setCampaignSource] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const isMobile = useIsMobile();

  // Add scroll to form function
  const scrollToForm = () => {
    const formSection = document.getElementById('cadastro');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Extract UTM parameters when component mounts
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('utm_source') || 'instagram'; // Default to instagram
    setCampaignSource(source);
    
    // Track page view - immediate
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'page_view', {
        page_title: 'Broker Novos Usuários',
        page_location: window.location.href,
        page_path: window.location.pathname,
        campaign_source: source
      });
    }
    
    // Signal that initial loading has completed
    setIsLoaded(true);
  }, []);

  // Lazy load image handling
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5,
        delay: i * 0.1,
        ease: "easeOut"
      }
    })
  };

  return (
    <div className="flex flex-col min-h-screen bg-white overflow-x-hidden">
      <GridBackground isDense={isMobile} />
      
      {/* Mobile-optimized Header */}
      <header className="py-4 px-4 flex justify-between items-center">
        <div className="flex items-center">
          <div className="bg-blue-dark p-1.5 rounded-md mr-2">
            <LineChart className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold text-xl text-blue-dark">Broker Base</span>
        </div>
        <Link to="/login">
          <Button variant="ghost" size="sm" className="text-blue-dark">
            Entrar
          </Button>
        </Link>
      </header>
      
      {/* Hero Section - Optimized for mobile */}
      <section className="pt-6 pb-10 px-4">
        <div className="container mx-auto max-w-md">
          <div className="flex flex-col items-center text-center">
            <motion.h1 
              custom={0}
              initial="hidden"
              animate={isLoaded ? "visible" : "hidden"}
              variants={fadeInUpVariants}
              className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-4"
            >
              Aumente Suas <span className="text-blue-dark">Vendas</span> com Análises Profissionais
            </motion.h1>
            
            <motion.p 
              custom={1}
              initial="hidden"
              animate={isLoaded ? "visible" : "hidden"}
              variants={fadeInUpVariants}
              className="text-lg text-gray-700 mb-6"
            >
              A plataforma que transforma corretores em consultores imobiliários de alta performance.
            </motion.p>
            
            {/* Add new Cadastre-se agora button */}
            <motion.button
              custom={2}
              initial="hidden"
              animate={isLoaded ? "visible" : "hidden"}
              variants={fadeInUpVariants}
              onClick={scrollToForm}
              className="bg-blue-dark hover:bg-blue-dark/90 text-white font-medium px-6 py-3 rounded-lg flex items-center gap-2 mb-8 mx-auto"
            >
              Cadastre-se agora
              <ArrowRight className="h-4 w-4" />
            </motion.button>
            
            {/* Hero Image with Skeleton */}
            <motion.div 
              custom={3} // Updated custom index
              initial="hidden"
              animate={isLoaded ? "visible" : "hidden"}
              variants={fadeInUpVariants}
              className="w-full rounded-xl overflow-hidden shadow-lg mb-8"
            >
              <AspectRatio ratio={16/9} className="bg-gray-100">
                {!imageLoaded && <Skeleton className="w-full h-full absolute inset-0" />}
                <img 
                  src="https://i.imgur.com/Pn1PxaL.png" 
                  alt="Dashboard de análise imobiliária"
                  className={`w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={handleImageLoad}
                  loading="eager" // Load this image immediately as it's above the fold
                />
              </AspectRatio>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section - Mobile First */}
      <section className="py-8 px-4 bg-blue-50">
        <div className="container mx-auto max-w-md">
          <motion.h2 
            custom={4} // Updated custom index
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            variants={fadeInUpVariants}
            className="text-2xl font-bold text-center mb-8"
          >
            Ferramentas que transformam sua negociação
          </motion.h2>
          
          <div className="space-y-4">
            {/* Benefit Cards */}
            {[
              {
                icon: <LineChart className="h-5 w-5 text-blue-dark" />,
                title: "Análises que aumentam sua CONVERSÃO",
                description: "Gere análises profissionais em segundos para fechar mais vendas."
              },
              {
                icon: <BarChart3 className="h-5 w-5 text-blue-dark" />,
                title: "Dashboard de Mercado Completo",
                description: "Acesse dados atualizados para negociações mais efetivas."
              },
              {
                icon: <Users className="h-5 w-5 text-blue-dark" />,
                title: "Destaque-se da concorrência",
                description: "Apresente dados que nenhum outro corretor da sua região tem."
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                custom={index + 5} // Updated custom index
                initial="hidden"
                animate={isLoaded ? "visible" : "hidden"}
                variants={fadeInUpVariants}
                className="bg-white p-4 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="flex items-start">
                  <div className="bg-blue-light/10 p-2 rounded-lg flex items-center justify-center mr-3">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm">{benefit.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section - Mobile First */}
      <section className="py-8 px-4 bg-white">
        <div className="container mx-auto max-w-md">
          <motion.h2
            custom={8} // Updated custom index
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            variants={fadeInUpVariants}
            className="text-2xl font-bold text-center mb-6"
          >
            O que você vai ganhar
          </motion.h2>
          
          <motion.div
            custom={9} // Updated custom index
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            variants={fadeInUpVariants}
            className="space-y-3 mb-8"
          >
            {[
              'Comparativos de preço por m²', 
              'Estimativa de valorização', 
              'Análise de bairros', 
              'Pontos fortes e fracos do imóvel'
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="bg-blue-100 rounded-full p-1 flex-shrink-0">
                  <Check className="h-4 w-4 text-blue-dark" />
                </div>
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* Sign Up Form Section */}
      <section id="cadastro" className="py-8 px-4 bg-blue-50">
        <div className="container mx-auto max-w-md">
          <motion.div
            custom={10} // Updated custom index
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            variants={fadeInUpVariants}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2">Crie sua conta gratuita</h2>
              <p className="text-gray-600 text-sm">
                Comece a usar o Broker Base em menos de 2 minutos
              </p>
            </div>
            
            <LeadCaptureForm campaignSource={campaignSource || 'instagram'} />
            
            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <p className="text-sm text-gray-600">
                Já tem uma conta?{' '}
                <Link to="/login" className="text-blue-dark font-medium">
                  Faça login
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Footer - Simplified for Mobile */}
      <footer className="bg-blue-dark text-white py-6 px-4 mt-auto">
        <div className="container mx-auto max-w-md">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="flex items-center">
              <LineChart className="h-5 w-5 mr-2" />
              <span className="font-bold text-lg">BrokerBase</span>
            </div>
            
            <div className="flex space-x-4 text-xs text-white/80">
              <Link to="/termos-de-uso" className="hover:text-white">
                Termos
              </Link>
              <Link to="/politica-de-privacidade" className="hover:text-white">
                Privacidade
              </Link>
              <Link to="/login" className="hover:text-white">
                Entrar
              </Link>
            </div>
            
            <div className="text-white/60 text-xs">
              &copy; {new Date().getFullYear()} BrokerBase
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BrokerNovosUsuarios;
